import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Card, Typography, message, Modal } from 'antd';
import { supabase } from '../config/supabase';
import { getFriendlyAuthError, signInWithEmailPassword } from '../auth/login';
import {
  PASSWORD_RESET_REQUEST_ACK,
  requestPasswordReset,
  resetPasswordRedirectUrl,
} from '../auth/passwordRecovery';

const { Title, Text } = Typography;

export interface EmailLoginPageLabels {
  email: string;
  emailRequired: string;
  password: string;
  passwordRequired: string;
  passwordPlaceholder: string;
  forgotPassword: string;
  logIn: string;
  haveResetLink: string;
  resetModalTitle: string;
  sendResetEmail: string;
  cancel: string;
  registeredEmail: string;
  enterValidEmail: string;
  resetSecurityNote: string;
  loginSuccess: string;
  loginFailed: string;
}

const DEFAULT_LABELS: EmailLoginPageLabels = {
  email: 'Email',
  emailRequired: 'Please input your email!',
  password: 'Password',
  passwordRequired: 'Please input your password!',
  passwordPlaceholder: 'Enter your password',
  forgotPassword: 'Forgot password?',
  logIn: 'Log In',
  haveResetLink: 'Have a reset link?',
  resetModalTitle: 'Reset password',
  sendResetEmail: 'Send reset email',
  cancel: 'Cancel',
  registeredEmail: 'Registered email',
  enterValidEmail: 'Enter a valid email',
  resetSecurityNote:
    'For security we always show the same confirmation message, whether or not the email is registered.',
  loginSuccess: 'Login successful!',
  loginFailed: 'Login failed',
};

export interface EmailLoginPageProps {
  portalLabel: string;
  redirectTo?: string;
  titleColor?: string;
  primaryColor?: string;
  emailPlaceholder?: string;
  /** Shown under the form for forgotten account guidance */
  supportHint?: string;
  /** Host-portal i18n strings (English defaults if omitted) */
  labels?: Partial<EmailLoginPageLabels>;
}

export function EmailLoginPage({
  portalLabel,
  redirectTo = '/dashboard',
  titleColor,
  primaryColor,
  emailPlaceholder = 'email@example.com',
  supportHint = 'Forgot which email you use? Contact your organisation admin or UBUS support.',
  labels: labelsProp,
}: EmailLoginPageProps) {
  const labels = { ...DEFAULT_LABELS, ...labelsProp };
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotForm] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        navigate(redirectTo);
      }
    });
  }, [navigate, redirectTo]);

  const handleLogin = async (values: { email: string; password: string }) => {
    setLoading(true);
    try {
      const { session, error } = await signInWithEmailPassword(values.email, values.password);

      if (error) {
        message.error(getFriendlyAuthError(error));
      } else if (session) {
        message.success(labels.loginSuccess);
        navigate(redirectTo);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : labels.loginFailed;
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (values: { email: string }) => {
    setForgotLoading(true);
    try {
      await requestPasswordReset(values.email, resetPasswordRedirectUrl());
      message.success(PASSWORD_RESET_REQUEST_ACK);
      setForgotOpen(false);
      forgotForm.resetFields();
    } finally {
      setForgotLoading(false);
    }
  };

  const buttonStyle = primaryColor
    ? { background: primaryColor, borderColor: primaryColor }
    : undefined;

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: '#f0f2f5',
    }}>
      <Card style={{ width: 400, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Title level={2} style={{ marginBottom: 8, ...(titleColor ? { color: titleColor } : {}) }}>
            UBUS
          </Title>
          <Text type="secondary">{portalLabel}</Text>
        </div>
        <Form name="login" onFinish={handleLogin} layout="vertical" size="large">
          <Form.Item
            name="email"
            label={labels.email}
            rules={[{ required: true, message: labels.emailRequired }]}
          >
            <Input placeholder={emailPlaceholder} autoComplete="email" />
          </Form.Item>
          <Form.Item
            name="password"
            label={labels.password}
            rules={[{ required: true, message: labels.passwordRequired }]}
          >
            <Input.Password placeholder={labels.passwordPlaceholder} autoComplete="current-password" />
          </Form.Item>
          <div style={{ marginBottom: 16, textAlign: 'right' }}>
            <Button type="link" style={{ padding: 0 }} onClick={() => setForgotOpen(true)}>
              {labels.forgotPassword}
            </Button>
          </div>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block style={buttonStyle}>
              {labels.logIn}
            </Button>
          </Form.Item>
        </Form>
        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
          {supportHint}
        </Text>
        <div style={{ marginTop: 8 }}>
          <Link to="/reset-password" style={{ fontSize: 12, color: '#8c8c8c' }}>
            {labels.haveResetLink}
          </Link>
        </div>
      </Card>

      <Modal
        title={labels.resetModalTitle}
        open={forgotOpen}
        onCancel={() => setForgotOpen(false)}
        onOk={() => forgotForm.submit()}
        confirmLoading={forgotLoading}
        okText={labels.sendResetEmail}
        cancelText={labels.cancel}
      >
        <Form form={forgotForm} layout="vertical" onFinish={(v) => void handleForgot(v)}>
          <Form.Item
            name="email"
            label={labels.registeredEmail}
            rules={[{ required: true, type: 'email', message: labels.enterValidEmail }]}
          >
            <Input autoComplete="email" />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {labels.resetSecurityNote}
          </Text>
        </Form>
      </Modal>
    </div>
  );
}

export default EmailLoginPage;
